import { useState } from 'react'
import { format, subDays } from 'date-fns'
import {
  useAnalyticsOverview,
  useAnalyticsAgents,
  useAnalyticsUsers,
  useAnalyticsTransactions,
  useAnalyticsMessages,
  useAnalyticsConversations,
} from '@/features/analytics/hooks/use-analytics'
import { useRateCard } from '../hooks/use-analytics'
import { AnalyticsFilters } from '@/features/analytics/components/AnalyticsFilters'
import { PageLayout } from '@/layouts/PageLayout'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert'
import { Skeleton } from '@/components/ui/skeleton'
import { Button } from '@/components/ui/button'
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover'
import { Calendar } from '@/components/ui/calendar'
import { Pagination } from '@/components/ui/pagination'
import { BarChart3, Users as UsersIcon, Bot, MessageSquare, TrendingUp, Activity, Calendar as CalendarIcon } from 'lucide-react'
import { cn } from '@/lib/utils'

export default function AnalyticsPage() {
  const [tab, setTab] = useState<'overview' | 'users' | 'agents' | 'transactions' | 'messages' | 'conversations' | 'rate-card'>('overview')

  // Date range state - default to last 7 days
  const [startDate, setStartDate] = useState<Date>(subDays(new Date(), 7))
  const [endDate, setEndDate] = useState<Date>(new Date())

  // Tenant/Org filter state
  const [filters, setFilters] = useState<{
    tenantId?: string
    organizationId?: string
  }>({})

  // Pagination state for each tab
  const [activityPage, setActivityPage] = useState(1)
  const [recentPage, setRecentPage] = useState(1)
  const [agentsPage, setAgentsPage] = useState(1)
  const [transactionsPage, setTransactionsPage] = useState(1)
  const [messagesPage, setMessagesPage] = useState(1)
  const [conversationsPage, setConversationsPage] = useState(1)

  const pageSize = 20

  // Format dates and filters for API
  const dateParams = {
    start: format(startDate, "yyyy-MM-dd'T'HH:mm:ss"),
    end: format(endDate, "yyyy-MM-dd'T'HH:mm:ss"),
    tenantId: filters.tenantId,
    organizationId: filters.organizationId,
  }

  // Use new React Query hooks - OPTIMIZED: Only fetch data for active tab
  const { data: overviewData, isLoading, error } = useAnalyticsOverview({ ...dateParams, enabled: tab === 'overview' })
  const { data: agentsData, isLoading: agentsLoading } = useAnalyticsAgents({ limit: pageSize, offset: (agentsPage - 1) * pageSize, ...dateParams, enabled: tab === 'agents' })
  const { data: usersActivityData, isLoading: usersActivityLoading } = useAnalyticsUsers({ limit: pageSize, offset: (activityPage - 1) * pageSize, sort_by: 'activity', ...dateParams, enabled: tab === 'users' })
  const { data: usersRecentData, isLoading: usersRecentLoading } = useAnalyticsUsers({ limit: pageSize, offset: (recentPage - 1) * pageSize, sort_by: 'joined', ...dateParams, enabled: tab === 'users' })
  const { data: txData, isLoading: txLoading } = useAnalyticsTransactions({ limit: pageSize, offset: (transactionsPage - 1) * pageSize, ...dateParams, enabled: tab === 'transactions' })
  const { data: msgsData, isLoading: msgsLoading } = useAnalyticsMessages({ limit: pageSize, offset: (messagesPage - 1) * pageSize, ...dateParams, enabled: tab === 'messages' })
  const { data: convsData, isLoading: convsLoading } = useAnalyticsConversations({ limit: pageSize, offset: (conversationsPage - 1) * pageSize, ...dateParams, enabled: tab === 'conversations' })
  const rateCard = useRateCard()

  // Extract data from React Query responses
  // Note: Backend returns data directly, not wrapped in {success, message, data}
  const data = overviewData
  const loading = isLoading

  const agents = { items: agentsData?.items || [], page: agentsData?.page || { total: 0, limit: pageSize, offset: 0 }, loading: agentsLoading, error: null }
  const usersActivity = { items: usersActivityData?.items || [], page: usersActivityData?.page || { total: 0, limit: pageSize, offset: 0 }, loading: usersActivityLoading, error: null }
  const usersRecent = { items: usersRecentData?.items || [], page: usersRecentData?.page || { total: 0, limit: pageSize, offset: 0 }, loading: usersRecentLoading, error: null }
  const tx = { items: txData?.items || [], page: txData?.page || { total: 0, limit: pageSize, offset: 0 }, loading: txLoading, error: null }
  const msgs = { items: msgsData?.items || [], page: msgsData?.page || { total: 0, limit: pageSize, offset: 0 }, loading: msgsLoading, error: null }
  const convs = { items: convsData?.items || [], page: convsData?.page || { total: 0, limit: pageSize, offset: 0 }, loading: convsLoading, error: null }

  // Quick preset date ranges
  const setDatePreset = (preset: 'today' | '7d' | '30d' | '90d') => {
    const now = new Date()
    setEndDate(now)

    switch (preset) {
      case 'today':
        setStartDate(new Date(now.setHours(0, 0, 0, 0)))
        break
      case '7d':
        setStartDate(subDays(now, 7))
        break
      case '30d':
        setStartDate(subDays(now, 30))
        break
      case '90d':
        setStartDate(subDays(now, 90))
        break
    }
  }

  const cards = [
    { label: 'Signups Today', value: data?.counts.signupsToday ?? 0, icon: UsersIcon, color: 'text-blue-600' },
    { label: 'Signups This Week', value: data?.counts.signupsWeek ?? 0, icon: TrendingUp, color: 'text-green-600' },
    { label: 'Agents Created Today', value: data?.counts.agentsCreatedToday ?? 0, icon: Bot, color: 'text-purple-600' },
    { label: 'Agents Created Week', value: data?.counts.agentsCreatedWeek ?? 0, icon: Bot, color: 'text-purple-500' },
    { label: 'Agents Used Today', value: data?.counts.agentsUsedToday ?? 0, icon: Activity, color: 'text-orange-600' },
    { label: 'Agents Used Week', value: data?.counts.agentsUsedWeek ?? 0, icon: Activity, color: 'text-orange-500' },
    { label: 'Transactions Today', value: data?.counts.transactionsToday ?? 0, icon: BarChart3, color: 'text-cyan-600' },
    { label: 'Transactions Week', value: data?.counts.transactionsWeek ?? 0, icon: BarChart3, color: 'text-cyan-500' },
    { label: 'Messages Today', value: data?.counts.messagesToday ?? 0, icon: MessageSquare, color: 'text-pink-600' },
    { label: 'Messages Week', value: data?.counts.messagesWeek ?? 0, icon: MessageSquare, color: 'text-pink-500' },
  ]

  // Format cost with consistent 6 decimal places
  const formatCost = (cost: number) => {
    return cost.toFixed(6)
  }

  const tokenCards = [
    { label: 'Prompt Tokens', value: data?.tokens.prompt ?? 0, color: 'text-blue-600' },
    { label: 'Completion Tokens', value: data?.tokens.completion ?? 0, color: 'text-green-600' },
    { label: 'Total Tokens', value: data?.tokens.total ?? 0, color: 'text-violet-600' },
    { label: 'Total Cost', value: formatCost(data?.tokens.totalValue ?? 0), color: 'text-amber-600', isCurrency: true },
  ]

  return (
    <PageLayout
      title="Analytics"
      description="Comprehensive insights into your usage and performance"
      icon={<BarChart3 className="h-6 w-6" />}
      actions={
          <div className="flex flex-col items-end gap-3">
            <div className="flex items-center gap-2 text-sm text-muted-foreground">
              <CalendarIcon className="h-4 w-4" />
              <span className="font-medium">
                {format(startDate, 'MMM d, yyyy')} - {format(endDate, 'MMM d, yyyy')}
              </span>
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <div className="flex items-center gap-1">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setDatePreset('today')}
                >
                  Today
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setDatePreset('7d')}
                >
                  7 Days
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setDatePreset('30d')}
                >
                  30 Days
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setDatePreset('90d')}
                >
                  90 Days
                </Button>
              </div>

              <div className="h-4 w-px bg-border" />

              <div className="flex items-center gap-1">
                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className={cn(
                        "justify-start text-left font-normal",
                        !startDate && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      Start: {startDate ? format(startDate, 'PP') : <span>Pick a date</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={startDate}
                      onSelect={(date) => date && setStartDate(date)}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>

                <Popover>
                  <PopoverTrigger asChild>
                    <Button
                      variant="outline"
                      size="sm"
                      className={cn(
                        "justify-start text-left font-normal",
                        !endDate && "text-muted-foreground"
                      )}
                    >
                      <CalendarIcon className="mr-2 h-4 w-4" />
                      End: {endDate ? format(endDate, 'PP') : <span>Pick a date</span>}
                    </Button>
                  </PopoverTrigger>
                  <PopoverContent className="w-auto p-0">
                    <Calendar
                      mode="single"
                      selected={endDate}
                      onSelect={(date) => date && setEndDate(date)}
                      initialFocus
                    />
                  </PopoverContent>
                </Popover>
              </div>
            </div>
          </div>
        }
      >

      {/* Analytics Filters - Tenant/Organization Scope */}
      <AnalyticsFilters
        filters={filters}
        onFiltersChange={setFilters}
      />

      <Tabs value={tab} onValueChange={(v) => setTab(v as typeof tab)} className="space-y-4">
        <TabsList>
          <TabsTrigger value="overview">Overview</TabsTrigger>
          <TabsTrigger value="conversations">Conversations</TabsTrigger>
          <TabsTrigger value="users">Users</TabsTrigger>
          <TabsTrigger value="agents">Agents</TabsTrigger>
          <TabsTrigger value="transactions">Transactions</TabsTrigger>
          <TabsTrigger value="messages">Messages</TabsTrigger>
          <TabsTrigger value="rate-card">Rate Card</TabsTrigger>
        </TabsList>

        <TabsContent value="overview" className="space-y-6">
          {loading && (
            <div className="space-y-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {[...Array(10)].map((_, i) => (
                  <Card key={i}>
                    <CardHeader className="pb-2">
                      <Skeleton className="h-4 w-24" />
                    </CardHeader>
                    <CardContent>
                      <Skeleton className="h-8 w-16" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            </div>
          )}

          {error && (
            <Alert variant="destructive">
              <AlertTitle>Error loading analytics</AlertTitle>
              <AlertDescription>
                {error instanceof Error ? error.message : String(error)}
              </AlertDescription>
            </Alert>
          )}

          {!loading && !error && data && (
            <>
              <div>
                <h2 className="text-xl font-semibold mb-4">Activity Metrics</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-5 gap-4">
                  {cards.map((c) => {
                    const Icon = c.icon
                    return (
                      <Card key={c.label} className="hover:shadow-md transition-shadow">
                        <CardHeader className="pb-2">
                          <div className="flex items-center justify-between">
                            <CardDescription className="text-xs">{c.label}</CardDescription>
                            <Icon className={`h-4 w-4 ${c.color}`} />
                          </div>
                        </CardHeader>
                        <CardContent>
                          <div className={`text-2xl font-bold ${c.color}`}>{c.value.toLocaleString()}</div>
                        </CardContent>
                      </Card>
                    )
                  })}
                </div>
              </div>

              <div>
                <h2 className="text-xl font-semibold mb-4">Token Usage & Cost</h2>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
                  {tokenCards.map((c: any) => (
                    <Card key={c.label} className="hover:shadow-md transition-shadow">
                      <CardHeader className="pb-2">
                        <CardDescription className="text-xs">{c.label}</CardDescription>
                      </CardHeader>
                      <CardContent>
                        <div className={`text-2xl font-bold ${c.color}`}>
                          {c.isCurrency ? `$${c.value}` : (typeof c.value === 'number' ? c.value.toLocaleString() : c.value)}
                        </div>
                      </CardContent>
                    </Card>
                  ))}
                </div>
              </div>

              {data.top && (
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Top Agents</CardTitle>
                      <CardDescription>Most active agents this week</CardDescription>
                    </CardHeader>
                    <CardContent>
                      {data.top.agents.length === 0 ? (
                        <p className="text-sm text-muted-foreground">No agent data available</p>
                      ) : (
                        <div className="space-y-2">
                          {data.top.agents.map((agent: any) => (
                            <div key={agent.id} className="flex items-center justify-between py-2 border-b last:border-0">
                              <span className="font-medium">{agent.name}</span>
                              <span className="text-sm text-muted-foreground">{agent.conversations} conversations</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-lg">Top Models</CardTitle>
                      <CardDescription>Most used models this week</CardDescription>
                    </CardHeader>
                    <CardContent>
                      {data.top.models.length === 0 ? (
                        <p className="text-sm text-muted-foreground">No model data available</p>
                      ) : (
                        <div className="space-y-2">
                          {data.top.models.map((model: any) => (
                            <div key={model.model} className="flex items-center justify-between py-2 border-b last:border-0">
                              <span className="font-medium">{model.model}</span>
                              <span className="text-sm text-muted-foreground">{model.count} uses</span>
                            </div>
                          ))}
                        </div>
                      )}
                    </CardContent>
                  </Card>
                </div>
              )}
            </>
          )}
        </TabsContent>

        <TabsContent value="agents">
          {agents.loading && (
            <Card>
              <CardContent className="py-10">
                <div className="flex items-center justify-center">
                  <div className="text-center">
                    <Skeleton className="h-8 w-32 mx-auto mb-2" />
                    <Skeleton className="h-4 w-48 mx-auto" />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {agents.error && (
            <Alert variant="destructive">
              <AlertTitle>Error loading agents</AlertTitle>
              <AlertDescription>{agents.error}</AlertDescription>
            </Alert>
          )}

          {!agents.loading && !agents.error && (
            <Card>
              <CardHeader>
                <CardTitle>Agent Usage</CardTitle>
                <CardDescription>Detailed agent performance and engagement metrics</CardDescription>
              </CardHeader>
              <CardContent>
                {agents.items.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <Bot className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>No agent data available</p>
                  </div>
                ) : (
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Agent Name</TableHead>
                          <TableHead>Category</TableHead>
                          <TableHead className="text-right">Conversations</TableHead>
                          <TableHead className="text-right">Users</TableHead>
                          <TableHead className="text-right">Last Active</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {agents.items.map((a) => (
                          <TableRow key={a.id}>
                            <TableCell>
                              <div>
                                <div className="font-medium">{a.name === "ephemeral"? "<Model Direct Chat> "+a.name:a.name}</div>
                                {a.description && (
                                  <div className="text-xs text-muted-foreground line-clamp-1 mt-1">
                                    {a.description}
                                  </div>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              {a.category && (
                                <span className="inline-flex items-center rounded-md bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-700/10">
                                  {a.category}
                                </span>
                              )}
                            </TableCell>
                            <TableCell className="text-right">{a.conversations.toLocaleString()}</TableCell>
                            <TableCell className="text-right">{a.uniqueUsers.toLocaleString()}</TableCell>
                            <TableCell className="text-right text-sm text-muted-foreground">
                              {a.lastActive ? new Date(a.lastActive).toLocaleDateString() : '-'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="users">
          <Tabs defaultValue="activity" className="w-full">
            <TabsList className="grid w-full grid-cols-2 mb-4">
              <TabsTrigger value="activity">Activity</TabsTrigger>
              <TabsTrigger value="recent">Recent Joiners</TabsTrigger>
            </TabsList>

            {/* Activity Tab */}
            <TabsContent value="activity">
              {usersActivity.loading && (
                <Card>
                  <CardContent className="py-10">
                    <div className="flex items-center justify-center">
                      <div className="text-center">
                        <Skeleton className="h-8 w-32 mx-auto mb-2" />
                        <Skeleton className="h-4 w-48 mx-auto" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {usersActivity.error && (
                <Alert variant="destructive">
                  <AlertTitle>Error loading user activity</AlertTitle>
                  <AlertDescription>{usersActivity.error}</AlertDescription>
                </Alert>
              )}

              {!usersActivity.loading && !usersActivity.error && (
                <Card>
                  <CardHeader>
                    <CardTitle>Most Active Users</CardTitle>
                    <CardDescription>Users sorted by activity (conversations, messages, tokens)</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {usersActivity.items.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <UsersIcon className="h-12 w-12 mx-auto mb-2 opacity-50" />
                        <p>No user activity data available</p>
                      </div>
                    ) : (
                      <>
                        <div className="rounded-md border overflow-x-auto">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead className="w-16">Rank</TableHead>
                                <TableHead>User</TableHead>
                                <TableHead className="text-right">Conversations</TableHead>
                                <TableHead className="text-right">Messages</TableHead>
                                <TableHead className="text-right">Agents Used</TableHead>
                                <TableHead className="text-right">Tokens</TableHead>
                                <TableHead className="text-right">Cost</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {usersActivity.items.map((u: any, index: number) => (
                                <TableRow key={u.id}>
                                  <TableCell className="font-medium text-muted-foreground">
                                    #{(activityPage - 1) * pageSize + index + 1}
                                  </TableCell>
                                  <TableCell>
                                    <div className="text-sm">
                                      <div className="font-medium">{u.name || u.username || 'Unknown'}</div>
                                      <div className="text-xs text-muted-foreground">{u.email}</div>
                                    </div>
                                  </TableCell>
                                  <TableCell className="text-right font-medium">{u.conversations}</TableCell>
                                  <TableCell className="text-right">{u.messages}</TableCell>
                                  <TableCell className="text-right">{u.uniqueAgents}</TableCell>
                                  <TableCell className="text-right">
                                    <div className="text-sm">
                                      <div className="font-medium">{u.totalTokens.toLocaleString()}</div>
                                      <div className="text-xs text-muted-foreground">
                                        {u.promptTokens.toLocaleString()}↑ / {u.completionTokens.toLocaleString()}↓
                                      </div>
                                    </div>
                                  </TableCell>
                                  <TableCell className="text-right font-medium text-amber-600">
                                    ${formatCost(u.totalCost)}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>

                        <Pagination
                          currentPage={activityPage}
                          totalItems={usersActivity.page.total}
                          pageSize={pageSize}
                          onPageChange={setActivityPage}
                          className="mt-4"
                        />
                      </>
                    )}
                  </CardContent>
                </Card>
              )}
            </TabsContent>

            {/* Recent Joiners Tab */}
            <TabsContent value="recent">
              {usersRecent.loading && (
                <Card>
                  <CardContent className="py-10">
                    <div className="flex items-center justify-center">
                      <div className="text-center">
                        <Skeleton className="h-8 w-32 mx-auto mb-2" />
                        <Skeleton className="h-4 w-48 mx-auto" />
                      </div>
                    </div>
                  </CardContent>
                </Card>
              )}

              {usersRecent.error && (
                <Alert variant="destructive">
                  <AlertTitle>Error loading recent joiners</AlertTitle>
                  <AlertDescription>{usersRecent.error}</AlertDescription>
                </Alert>
              )}

              {!usersRecent.loading && !usersRecent.error && (
                <Card>
                  <CardHeader>
                    <CardTitle>Recent Joiners</CardTitle>
                    <CardDescription>Newest users ordered by join date</CardDescription>
                  </CardHeader>
                  <CardContent>
                    {usersRecent.items.length === 0 ? (
                      <div className="text-center py-8 text-muted-foreground">
                        <UsersIcon className="h-12 w-12 mx-auto mb-2 opacity-50" />
                        <p>No recent joiners data available</p>
                      </div>
                    ) : (
                      <>
                        <div className="rounded-md border overflow-x-auto">
                          <Table>
                            <TableHeader>
                              <TableRow>
                                <TableHead>User</TableHead>
                                <TableHead>Joined</TableHead>
                                <TableHead className="text-right">Conversations</TableHead>
                                <TableHead className="text-right">Messages</TableHead>
                                <TableHead className="text-right">Agents Used</TableHead>
                                <TableHead className="text-right">Tokens</TableHead>
                                <TableHead className="text-right">Cost</TableHead>
                              </TableRow>
                            </TableHeader>
                            <TableBody>
                              {usersRecent.items.map((u: any) => (
                                <TableRow key={u.id}>
                                  <TableCell>
                                    <div className="text-sm">
                                      <div className="font-medium">{u.name || u.username || 'Unknown'}</div>
                                      <div className="text-xs text-muted-foreground">{u.email}</div>
                                    </div>
                                  </TableCell>
                                  <TableCell>
                                    <div className="text-sm">
                                      {u.createdAt ? new Date(u.createdAt).toLocaleDateString() : '-'}
                                    </div>
                                  </TableCell>
                                  <TableCell className="text-right font-medium">{u.conversations}</TableCell>
                                  <TableCell className="text-right">{u.messages}</TableCell>
                                  <TableCell className="text-right">{u.uniqueAgents}</TableCell>
                                  <TableCell className="text-right">
                                    <div className="text-sm">
                                      <div className="font-medium">{u.totalTokens.toLocaleString()}</div>
                                      <div className="text-xs text-muted-foreground">
                                        {u.promptTokens.toLocaleString()}↑ / {u.completionTokens.toLocaleString()}↓
                                      </div>
                                    </div>
                                  </TableCell>
                                  <TableCell className="text-right font-medium text-amber-600">
                                    ${formatCost(u.totalCost)}
                                  </TableCell>
                                </TableRow>
                              ))}
                            </TableBody>
                          </Table>
                        </div>

                        <Pagination
                          currentPage={recentPage}
                          totalItems={usersRecent.page.total}
                          pageSize={pageSize}
                          onPageChange={setRecentPage}
                          className="mt-4"
                        />
                      </>
                    )}
                  </CardContent>
                </Card>
              )}
            </TabsContent>
          </Tabs>
        </TabsContent>

        <TabsContent value="transactions">
          {tx.loading && (
            <Card>
              <CardContent className="py-10">
                <div className="flex items-center justify-center">
                  <div className="text-center">
                    <Skeleton className="h-8 w-32 mx-auto mb-2" />
                    <Skeleton className="h-4 w-48 mx-auto" />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {tx.error && (
            <Alert variant="destructive">
              <AlertTitle>Error loading transactions</AlertTitle>
              <AlertDescription>{tx.error}</AlertDescription>
            </Alert>
          )}

          {!tx.loading && !tx.error && (
            <Card>
              <CardHeader>
                <CardTitle>Transaction Summary</CardTitle>
                <CardDescription>Token usage breakdown by model</CardDescription>
              </CardHeader>
              <CardContent>
                {tx.items.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <BarChart3 className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>No transaction data available</p>
                  </div>
                ) : (
                  <div className="rounded-md border overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Model</TableHead>
                          <TableHead className="text-right">Count</TableHead>
                          <TableHead className="text-right">Total Tokens</TableHead>
                          <TableHead className="text-right">Token Breakdown</TableHead>
                          <TableHead className="text-right">Cost</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {tx.items.map((r: any) => (
                          <TableRow key={r.model}>
                            <TableCell className="font-medium text-xs">{r.model || 'unknown'}</TableCell>
                            <TableCell className="text-right">{r.totals.count.toLocaleString()}</TableCell>
                            <TableCell className="text-right font-medium">{r.totals.tokens.total.toLocaleString()}</TableCell>
                            <TableCell className="text-right text-xs text-muted-foreground">
                              {r.totals.tokens.prompt.toLocaleString()}↑ / {r.totals.tokens.completion.toLocaleString()}↓
                            </TableCell>
                            <TableCell className="text-right font-medium text-amber-600">
                              ${formatCost(r.totals.value)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="messages">
          {msgs.loading && (
            <Card>
              <CardContent className="py-10">
                <div className="flex items-center justify-center">
                  <div className="text-center">
                    <Skeleton className="h-8 w-32 mx-auto mb-2" />
                    <Skeleton className="h-4 w-48 mx-auto" />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {msgs.error && (
            <Alert variant="destructive">
              <AlertTitle>Error loading messages</AlertTitle>
              <AlertDescription>{msgs.error}</AlertDescription>
            </Alert>
          )}

          {!msgs.loading && !msgs.error && (
            <Card>
              <CardHeader>
                <CardTitle>Message Analytics</CardTitle>
                <CardDescription>Individual messages with input/output token breakdown and costs</CardDescription>
              </CardHeader>
              <CardContent>
                {msgs.items.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <MessageSquare className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>No message data available</p>
                  </div>
                ) : (
                  <div className="rounded-md border">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Direction</TableHead>
                          <TableHead>Sender</TableHead>
                          <TableHead>Text Preview</TableHead>
                          <TableHead className="text-right">Tokens (In/Out)</TableHead>
                          <TableHead className="text-right">Model</TableHead>
                          <TableHead className="text-right">Cost</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {msgs.items.map((m: any) => (
                          <TableRow key={m.messageId}>
                            <TableCell>
                              <div className="flex items-center gap-1">
                                {m.isInput ? (
                                  <span className="text-blue-600" title="Input">↑</span>
                                ) : (
                                  <span className="text-green-600" title="Output">↓</span>
                                )}
                                <span className="text-xs text-muted-foreground">
                                  {m.isInput ? 'In' : 'Out'}
                                </span>
                              </div>
                            </TableCell>
                            <TableCell className="text-sm">{m.sender}</TableCell>
                            <TableCell className="max-w-xs truncate text-xs text-muted-foreground">
                              {m.textGlimpse || <span className="italic">No text</span>}
                            </TableCell>
                            <TableCell className="text-right text-xs">
                              <div className="flex flex-col items-end">
                                {!!m.inputTokens && <span className="text-blue-600">{m.inputTokens.toLocaleString()}↑</span>}
                                {!!m.outputTokens && <span className="text-green-600">{m.outputTokens.toLocaleString()}↓</span>}
                              </div>
                            </TableCell>
                            <TableCell className="text-right text-xs font-mono">
                              {m.model || 'unknown'}
                            </TableCell>
                            <TableCell className="text-right font-medium text-amber-600">
                              ${formatCost(m.cost)}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="conversations">
          {convs.loading && (
            <Card>
              <CardContent className="py-10">
                <div className="flex items-center justify-center">
                  <div className="text-center">
                    <Skeleton className="h-8 w-32 mx-auto mb-2" />
                    <Skeleton className="h-4 w-48 mx-auto" />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {convs.error && (
            <Alert variant="destructive">
              <AlertTitle>Error loading conversations</AlertTitle>
              <AlertDescription>{convs.error}</AlertDescription>
            </Alert>
          )}

          {!convs.loading && !convs.error && (
            <Card>
              <CardHeader>
                <CardTitle>Conversation Analytics</CardTitle>
                <CardDescription>
                  Detailed conversation-level metrics with user, agent, tokens, and latency data
                </CardDescription>
              </CardHeader>
              <CardContent>
                {convs.items.length === 0 ? (
                  <div className="text-center py-8 text-muted-foreground">
                    <MessageSquare className="h-12 w-12 mx-auto mb-2 opacity-50" />
                    <p>No conversation data available</p>
                  </div>
                ) : (
                  <div className="rounded-md border overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>User</TableHead>
                          <TableHead>Agent</TableHead>
                          <TableHead>Model</TableHead>
                          <TableHead className="text-right">Messages</TableHead>
                          <TableHead className="text-right">Total Tokens</TableHead>
                          <TableHead className="text-right">Avg Latency (ms)</TableHead>
                          <TableHead className="text-right">Started</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {convs.items.map((c: any) => (
                          <TableRow key={c.conversationId}>
                            <TableCell>
                              <div className="text-sm">
                                <div className="font-medium">
                                  {c.userName || c.userEmail || 'Unknown'}
                                </div>
                                {c.userEmail && (
                                  <div className="text-xs text-muted-foreground">
                                    {c.userEmail}
                                  </div>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="text-sm">
                                {c.agentName ? (
                                  <>
                                    <div className="font-medium">{c.agentName}</div>
                                    {c.isCustomAgent && (
                                      <span className="inline-flex items-center rounded-md bg-purple-50 px-1.5 py-0.5 text-xs font-medium text-purple-700 ring-1 ring-inset ring-purple-700/10 mt-1">
                                        Custom
                                      </span>
                                    )}
                                  </>
                                ) : c.agentId === 'ephemeral' ? (
                                  <span className="text-muted-foreground text-italic">{`<Direct Model>`}</span>
                                ) : (
                                  <span className="text-muted-foreground text-italic">N/A</span>
                                )}
                              </div>
                            </TableCell>
                            <TableCell>
                              <div className="text-xs font-mono max-w-[200px] truncate">
                                {c.model || '-'}
                              </div>
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="text-sm">
                                <div>{c.totalMessageCount}</div>
                                <div className="text-xs text-muted-foreground">
                                  {c.userMessageCount}↑ / {c.assistantMessageCount}↓
                                </div>
                              </div>
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="text-sm">
                                <div className="font-medium">
                                  {c.totalTokens.toLocaleString()}
                                </div>
                                <div className="text-xs text-muted-foreground">
                                  {c.promptTokens.toLocaleString()}↑ / {c.completionTokens.toLocaleString()}↓
                                </div>
                                {c.totalValue > 0 && (
                                  <div className="text-xs font-medium text-amber-600 mt-0.5">
                                    ${formatCost(c.totalValue)}
                                  </div>
                                )}
                              </div>
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="text-sm font-medium">
                                {c.averageTurnLatencyMs > 0 ? (
                                  <>
                                  <span className={
                                    c.averageTurnLatencyMs < 1000 ? 'text-green-600' :
                                    c.averageTurnLatencyMs < 5000 ? 'text-yellow-600' :
                                    'text-red-600'
                                  }>
                                    {c.averageTurnLatencyMs.toLocaleString()}
                                  </span>
                                  <span className='text-muted-foreground font-light text-italic ml-1'>ms</span>
                                  </>
                                  
                                ) : '-'}
                              </div>
                            </TableCell>
                            <TableCell className="text-right text-xs text-muted-foreground">
                              {c.conversationStartedAt
                                ? new Date(c.conversationStartedAt).toLocaleDateString()
                                : '-'}
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </TabsContent>

        <TabsContent value="rate-card">
          {rateCard.loading && (
            <Card>
              <CardContent className="py-10">
                <div className="flex items-center justify-center">
                  <div className="text-center">
                    <Skeleton className="h-8 w-32 mx-auto mb-2" />
                    <Skeleton className="h-4 w-48 mx-auto" />
                  </div>
                </div>
              </CardContent>
            </Card>
          )}

          {rateCard.error && (
            <Alert variant="destructive">
              <AlertTitle>Error loading rate card</AlertTitle>
              <AlertDescription>{rateCard.error}</AlertDescription>
            </Alert>
          )}

          {!rateCard.loading && !rateCard.error && rateCard.data && (
            <div className="space-y-6">
              <Card>
                <CardHeader>
                  <CardTitle>Model Pricing Rate Card</CardTitle>
                  <CardDescription>
                    Static pricing rates for all supported models. Rates are per 1 million tokens.
                  </CardDescription>
                </CardHeader>
                <CardContent>
                  <div className="rounded-md border overflow-x-auto">
                    <Table>
                      <TableHeader>
                        <TableRow>
                          <TableHead>Model</TableHead>
                          <TableHead>Provider</TableHead>
                          <TableHead>Tier</TableHead>
                          <TableHead className="text-right">Input ($/1M tokens)</TableHead>
                          <TableHead className="text-right">Output ($/1M tokens)</TableHead>
                          <TableHead>Notes</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {rateCard.data.rates
                          .sort((a: any, b: any) => a.provider.localeCompare(b.provider))
                          .map((rate: any) => (
                            <TableRow key={rate.model}>
                              <TableCell className="font-mono text-xs font-medium">{rate.model}</TableCell>
                              <TableCell>
                                <span className="inline-flex items-center rounded-md bg-blue-50 px-2 py-1 text-xs font-medium text-blue-700 ring-1 ring-inset ring-blue-700/10">
                                  {rate.provider}
                                </span>
                              </TableCell>
                              <TableCell>
                                <span className={`inline-flex items-center rounded-md px-2 py-1 text-xs font-medium ring-1 ring-inset ${
                                  rate.tier === 'premium' ? 'bg-purple-50 text-purple-700 ring-purple-700/10' :
                                  rate.tier === 'standard' ? 'bg-green-50 text-green-700 ring-green-700/10' :
                                  rate.tier === 'open-source' ? 'bg-cyan-50 text-cyan-700 ring-cyan-700/10' :
                                  rate.tier === 'specialized' ? 'bg-orange-50 text-orange-700 ring-orange-700/10' :
                                  'bg-gray-50 text-gray-700 ring-gray-700/10'
                                }`}>
                                  {rate.tier}
                                </span>
                              </TableCell>
                              <TableCell className="text-right font-medium text-green-600">
                                ${rate.input_usd_per_million.toFixed(2)}
                              </TableCell>
                              <TableCell className="text-right font-medium text-amber-600">
                                ${rate.output_usd_per_million.toFixed(2)}
                              </TableCell>
                              <TableCell className="text-xs text-muted-foreground max-w-md">
                                {rate.notes}
                              </TableCell>
                            </TableRow>
                          ))}
                      </TableBody>
                    </Table>
                  </div>
                </CardContent>
              </Card>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <Card>
                  <CardHeader>
                    <CardTitle>Pricing by Tier</CardTitle>
                    <CardDescription>Models grouped by pricing tier</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {Object.entries(rateCard.data.byTier || {}).map(([tier, models]: [string, any]) => (
                        <div key={tier} className="space-y-2">
                          <h4 className="font-medium text-sm capitalize">{tier} ({models.length} models)</h4>
                          <div className="flex flex-wrap gap-2">
                            {models.map((m: any) => (
                              <span key={m.model} className="inline-flex items-center rounded-md bg-gray-50 px-2 py-1 text-xs font-medium text-gray-700 ring-1 ring-inset ring-gray-700/10">
                                {m.model}
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>

                <Card>
                  <CardHeader>
                    <CardTitle>Pricing by Provider</CardTitle>
                    <CardDescription>Models grouped by provider</CardDescription>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {Object.entries(rateCard.data.byProvider || {}).map(([provider, models]: [string, any]) => (
                        <div key={provider} className="space-y-2">
                          <h4 className="font-medium text-sm">{provider} ({models.length} models)</h4>
                          <div className="flex flex-wrap gap-2">
                            {models.map((m: any) => (
                              <span key={m.model} className="inline-flex items-center rounded-md bg-gray-50 px-2 py-1 text-xs font-medium text-gray-700 ring-1 ring-inset ring-gray-700/10">
                                {m.model}
                              </span>
                            ))}
                          </div>
                        </div>
                      ))}
                    </div>
                  </CardContent>
                </Card>
              </div>
            </div>
          )}
        </TabsContent>
      </Tabs>
    </PageLayout>
  )
}


